import { AlertProps, Box, CloseButton, Icon, Text } from '@chakra-ui/react';
import React from 'react';

import { getStatusIcon } from '../utils';

export type InfoBoxVariant = 'info' | 'success' | 'warning' | 'error';

export interface InfoBoxProps extends Omit<AlertProps, 'status' | 'variant'> {
  /**
   * The variant of the InfoBox
   */
  variant: InfoBoxVariant;
  /**
   * The title of the InfoBox (optional)
   */
  title?: string;
  /**
   * The main message/content of the InfoBox
   */
  children: React.ReactNode;
  /**
   * Whether the InfoBox can be dismissed
   */
  dismissible?: boolean;
  /**
   * Callback fired when the close button is clicked
   */
  onClose?: () => void;
  /**
   * Size variant of the InfoBox
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Style variant of the InfoBox
   */
  styleVariant?: 'subtle' | 'solid' | 'left-accent';
}

const sizeConfig = {
  sm: {
    padding: 2,
    iconSize: 4,
    titleSize: 'sm',
    textSize: 'xs',
    iconPadding: 1.5,
  },
  md: {
    padding: 3,
    iconSize: 5,
    titleSize: 'md',
    textSize: 'sm',
    iconPadding: 2,
  },
  lg: {
    padding: 4,
    iconSize: 6,
    titleSize: 'lg',
    textSize: 'md',
    iconPadding: 2.5,
  },
};

export const InfoBox: React.FC<InfoBoxProps> = ({
  variant,
  title,
  children,
  dismissible = false,
  onClose,
  size = 'md',
  styleVariant = 'subtle',
  ...props
}) => {
  const config = sizeConfig[size];
  const icon = getStatusIcon(variant);

  // Define colors based on theme's color system (similar to Alert component)
  const getVariantColors = (variant: InfoBoxVariant) => {
    switch (variant) {
      case 'success':
        return {
          subtleBg: '#DEF0D6',
          solidBg: '#00A34B',
          iconColor: '#005527',
          borderColor: '#00A34B',
          titleColor: '#000027',
        };
      case 'warning':
        return {
          subtleBg: '#FAECD6',
          solidBg: '#E28800',
          iconColor: '#E28800',
          borderColor: '#E28800',
          titleColor: '#000000',
        };
      case 'info':
        return {
          subtleBg: '#D6D6F0',
          solidBg: '#0000A3',
          iconColor: '#00003D',
          borderColor: '#0000A3',
          titleColor: '#00003D',
        };
      case 'error':
        return {
          subtleBg: '#FFD6D6',
          solidBg: '#B80000',
          iconColor: '#B80000',
          borderColor: '#B80000',
          titleColor: '#000000',
        };
      default:
        return {
          subtleBg: '#D6D6F0',
          solidBg: '#0000A3',
          iconColor: '#00003D',
          borderColor: '#0000A3',
          titleColor: '#00003D',
        };
    }
  };

  const colors = getVariantColors(variant);

  // Style configurations based on styleVariant
  const getStyleConfig = () => {
    switch (styleVariant) {
      case 'solid':
        return {
          bg: colors.solidBg,
          borderColor: colors.borderColor,
          borderWidth: '1px',
          color: 'white',
          iconBg: 'whiteAlpha.200',
          iconColor: 'white',
          titleColor: 'white',
          textColor: 'white',
        };
      case 'left-accent':
        return {
          bg: colors.subtleBg,
          borderLeftColor: colors.borderColor,
          borderLeftWidth: '4px',
          borderColor: 'gray.200',
          borderWidth: '1px',
          color: colors.titleColor,
          iconBg: 'transparent',
          iconColor: colors.iconColor,
          titleColor: colors.titleColor,
          textColor: colors.titleColor,
        };
      case 'subtle':
      default:
        return {
          bg: colors.subtleBg,
          borderColor: colors.borderColor,
          borderWidth: '1px',
          color: colors.titleColor,
          iconBg: 'transparent',
          iconColor: colors.iconColor,
          titleColor: colors.titleColor,
          textColor: colors.titleColor,
        };
    }
  };

  const styleConfig = getStyleConfig();

  const borderStyles =
    styleVariant === 'left-accent'
      ? {
          border: '1px solid',
          borderColor: styleConfig.borderColor,
          borderLeft: '4px solid',
          borderLeftColor: styleConfig.borderLeftColor,
        }
      : {
          border: '1px solid',
          borderColor: styleConfig.borderColor,
        };

  return (
    <Box
      bg={styleConfig.bg}
      {...borderStyles}
      borderRadius="md"
      p={config.padding}
      display="flex"
      alignItems={title ? 'flex-start' : 'center'}
      boxShadow="sm"
      _hover={{
        boxShadow: 'md',
      }}
      transition="box-shadow 0.2s"
      {...props}
    >
      <Box
        bg={styleConfig.iconBg}
        borderRadius="full"
        p={config.iconPadding}
        mr={3}
        mt={0.5}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          as={icon}
          w={config.iconSize}
          h={config.iconSize}
          color={styleConfig.iconColor}
        />
      </Box>

      {/* Content */}
      <Box flex="1" minW={0}>
        {title && (
          <Text
            fontSize={config.titleSize}
            fontWeight="semibold"
            color={styleConfig.titleColor}
            mb={1}
            lineHeight="shorter"
          >
            {title}
          </Text>
        )}
        <Text
          fontSize={config.textSize}
          color={styleConfig.textColor}
          lineHeight="relaxed"
        >
          {children}
        </Text>
      </Box>

      {/* Close button */}
      {dismissible && onClose && (
        <CloseButton
          size="sm"
          onClick={onClose}
          ml={2}
          color={styleConfig.iconColor}
          _hover={{
            bg: styleConfig.iconBg,
          }}
          _focus={{
            boxShadow: `0 0 0 2px ${styleConfig.iconColor}`,
          }}
          borderRadius="md"
        />
      )}
    </Box>
  );
};
